
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { MailCheck, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { DeepInterviewState } from "@/hooks/a2a/screening/useDeepInterview";

interface TechnicalInterviewTabProps {
  deepInterview: DeepInterviewState;
  isLoading: boolean;
  onStartInterview: () => Promise<void>;
}

export function TechnicalInterviewTab({
  deepInterview,
  isLoading,
  onStartInterview
}: TechnicalInterviewTabProps) {
  const [followUpQuestion, setFollowUpQuestion] = useState("");
  const [isAskingQuestion, setIsAskingQuestion] = useState(false);
  
  const handleNextQuestion = async () => {
    if (!followUpQuestion.trim()) {
      toast.error("Please enter a follow-up question");
      return;
    }
    
    setIsAskingQuestion(true);
    // Simulate sending the question and getting a response
    setTimeout(() => {
      toast.success("Question sent and response received!");
      setIsAskingQuestion(false);
      setFollowUpQuestion("");
    }, 2000);
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">Technical Interview</h3>
      
      {deepInterview.status === 'idle' && (
        <Button onClick={onStartInterview} disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Starting...
            </>
          ) : (
            <>
              <MailCheck className="mr-2 h-4 w-4" />
              Start Interview
            </>
          )}
        </Button>
      )}
      
      {deepInterview.status === 'in-progress' && (
        <div className="space-y-2">
          <Badge variant="secondary">In Progress</Badge>
          
          {deepInterview.followupQuestions && deepInterview.followupQuestions.length > 0 ? (
            <div className="space-y-2">
              <h4 className="text-sm font-medium">
                Question {deepInterview.currentQuestion + 1}:
              </h4>
              <p className="text-sm">
                {deepInterview.followupQuestions[0]?.question || 'Generating questions...'}
              </p>
              
              <div className="flex items-center space-x-2">
                <Input
                  type="text"
                  placeholder="Your follow-up question"
                  value={followUpQuestion}
                  onChange={(e) => setFollowUpQuestion(e.target.value)}
                  disabled={isAskingQuestion}
                />
                <Button 
                  size="sm" 
                  onClick={handleNextQuestion} 
                  disabled={isAskingQuestion}
                >
                  {isAskingQuestion ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Asking...
                    </>
                  ) : (
                    "Ask"
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Generating follow-up questions...
            </p>
          )}
        </div>
      )}
      
      {deepInterview.status === 'completed' && (
        <div className="space-y-2">
          <Badge variant="success">Completed</Badge>
          <p className="text-sm text-muted-foreground">
            Technical interview completed. Proceed to fraud detection.
          </p>
        </div>
      )}
      
      {deepInterview.status === 'failed' && (
        <Badge variant="destructive">Failed</Badge>
      )}
    </div>
  );
}
